import React from 'react';
import { gql } from '@apollo/client';
import { Grid, Box, Typography } from 'vendor/material';
import { Banner, Checkbox, Link } from '@tackle-io/platform-ui';
import MsftCollapsibleFormSection from '../components/MsftCollapsibleFormSection';
import {
  AzureVendorAdditionalServicesFormSectionQuery,
  useAzureVendorAdditionalServicesFormSectionQuery,
  useUpdateAzureVendorAdditionalServicesFormSectionMutation,
} from 'generated/graphql';
import useStyles from '../../../../Settings/Account/marketplace/VendorConfig.styles';
import { FormProgress, FormProgressQueryResult } from 'utils/yup-extended';
import { xor } from 'lodash-es';
import { updatePayoutAndTaxProfiles } from '../utils/sharedSchemas';

// Using Azure query since we're using the same backend endpoint
export const AZURE_VENDOR_ADDITIONAL_SERVICES_FORM_SECTION_QUERY = gql`
  query AzureVendorAdditionalServicesFormSection {
    currentUser {
      id
      vendor {
        id
        onboarding {
          azure {
            commercial_marketplace
          }
        }
      }
    }
  }
`;

export const AZURE_VENDOR_ADDITIONAL_SERVICES_FORM_SECTION_MUTATION = gql`
  mutation UpdateAzureVendorAdditionalServicesFormSection(
    $updates: VendorInput!
  ) {
    updateVendor(updates: $updates) {
      id
      onboarding {
        azure {
          commercial_marketplace
        }
      }
    }
  }
`;

const calculateProgress = (
  data?: AzureVendorAdditionalServicesFormSectionQuery,
): FormProgress => {
  const commercial_marketplace =
    data?.currentUser?.vendor?.onboarding?.azure?.commercial_marketplace || [];

  let complete = 0;
  if (
    commercial_marketplace.includes('tax-profile') &&
    commercial_marketplace.includes('payout-profile')
  ) {
    complete = complete + 1;
  }

  if (commercial_marketplace.includes('marketing-profile')) {
    complete = complete + 1;
  }

  return {
    total: 2,
    complete,
  };
};

const VendorAdditionalServicesFormSection = ({
  icon,
}: {
  icon: JSX.Element;
}) => {
  const {
    data,
    loading: queryLoading,
    error,
  } = useAzureVendorAdditionalServicesFormSectionQuery({
    context: { batchKey: 'vendor-onboarding' },
  });
  const [updateValues, { loading: mutationLoading }] =
    useUpdateAzureVendorAdditionalServicesFormSectionMutation();

  const loading = mutationLoading || queryLoading;
  const disabled = Boolean(loading || error || !data?.currentUser?.vendor);
  const classes = useStyles();

  const { complete, total } = calculateProgress(data);
  const percentComplete = (complete / total) * 100;

  const commercial_marketplace =
    data?.currentUser?.vendor?.onboarding?.azure?.commercial_marketplace || [];

  const profilesAreComplete =
    commercial_marketplace.includes('tax-profile') &&
    commercial_marketplace.includes('payout-profile');

  return (
    <MsftCollapsibleFormSection
      icon={icon}
      slug="connect-additional-services"
      title="Connect to additional services"
      subTitle="Add your tax and payment information."
      percentComplete={percentComplete}
      loading={loading}
      permissions="Global Admin"
      timeToComplete="15 min/48 hr"
    >
      <Grid container direction="column">
        {(error || (!queryLoading && !data?.currentUser?.vendor)) && (
          <Grid item>
            <Box p={3}>
              <Banner
                title="Unable to load data"
                body="Something went wrong. Please refresh and try again or contact support."
                type="danger"
              />
            </Box>
          </Grid>
        )}
        <Grid item xs>
          <Box p={3} pb={2} className={classes.interGrid}>
            <Box mb={2} pb={2}>
              <Typography>
                Open these areas of the Partner Center to complete these tasks
                and connect to necessary services.
              </Typography>
            </Box>
            <Grid container direction="column">
              <Grid item>
                <Checkbox
                  disabled={disabled}
                  className={classes.checkBox}
                  label={
                    <Typography>
                      <b>Payout and tax profiles</b>
                    </Typography>
                  }
                  checked={profilesAreComplete}
                  onChange={() =>
                    updateValues({
                      variables: {
                        updates: {
                          id: data.currentUser.vendor.id,
                          onboarding: {
                            azure: {
                              commercial_marketplace:
                                updatePayoutAndTaxProfiles(
                                  commercial_marketplace,
                                ),
                            },
                          },
                        },
                      },
                    })
                  }
                />
              </Grid>
              <Grid item className={classes.textGrid}>
                <Box pl={4}>
                  <ol className={classes.ol}>
                    <Typography>
                      <li>
                        In the{' '}
                        <Link
                          external
                          to="https://partner.microsoft.com/en-us/dashboard/payee/profiles/partner/assign?="
                          className={classes.link}
                        >
                          Partner Center
                        </Link>
                        , under <b>Payout and tax profile</b> assignment, either
                        select an existing tax profile or create a new one. You
                        may need help from someone in your organization who can
                        provide financial information (like routing number, tax
                        ID or account types).
                      </li>
                      <li>
                        Once you have chosen the tax profile you want to use,
                        you will see a section for payment profiles. Complete
                        the payment profile with payment information (like bank
                        or PayPal).
                      </li>
                    </Typography>
                  </ol>
                </Box>
              </Grid>
            </Grid>
            <Grid container direction="column">
              <Grid item>
                <Checkbox
                  disabled={disabled}
                  className={classes.checkBox}
                  label={
                    <Typography>
                      <b>Business profile</b>
                    </Typography>
                  }
                  checked={commercial_marketplace.includes('marketing-profile')}
                  onChange={() =>
                    updateValues({
                      variables: {
                        updates: {
                          id: data.currentUser.vendor.id,
                          onboarding: {
                            azure: {
                              commercial_marketplace: xor(
                                commercial_marketplace,
                                ['marketing-profile'],
                              ),
                            },
                          },
                        },
                      },
                    })
                  }
                />
              </Grid>
              <Grid item className={classes.textGrid}>
                <Box pl={4}>
                  <ol className={classes.ol}>
                    <Typography>
                      <li>
                        In the Partner Center, under{' '}
                        <Link
                          external
                          to="https://partner.microsoft.com/en-us/referrals/businessprofiles/list"
                          className={classes.link}
                        >
                          Business Profile
                        </Link>
                        , select New Profile to create a new business profile.
                        If one already exists, please verify it is up to date.
                      </li>
                      <li>
                        In the window that opens, enter the required
                        information, accept the terms and conditions, and select{' '}
                        <b>Publish</b>.
                      </li>
                    </Typography>
                  </ol>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </MsftCollapsibleFormSection>
  );
};

export const useVendorAdditionalServicesFormSectionProgress =
  (): FormProgressQueryResult => {
    const { data, loading, error } =
      useAzureVendorAdditionalServicesFormSectionQuery({
        context: { batchKey: 'vendor-onboarding' },
      });
    const progress = calculateProgress(data);
    return { progress, loading, didError: Boolean(error) };
  };

export default VendorAdditionalServicesFormSection;
